import styled from '@emotion/styled';
import { Box } from '@mui/material';
import Image from 'next/image';
import { BodyLarge, BodyMedium, Center, Column, LabelSmall } from '../../styles';
import { Breakpoint, getBreakpointValue, isCompact } from '../../types';
import { shouldForwardProps } from '../../utils';

const UploadBox = styled(Center, shouldForwardProps)<{ isDraggingOver: boolean }>`
    background-color: ${({ isDraggingOver, theme }) =>
        isDraggingOver ? theme.colors.primaryContainer : theme.colors.surface};
    border: ${({ theme }) => `1px dashed ${theme.colors.outline}`};
    gap: ${({ theme }) => theme.spacing(0.5)};
    cursor: pointer;
`;

const UploadBoxSmall = styled(UploadBox, shouldForwardProps)`
    flex-direction: row;
    justify-content: flex-start;
    padding: ${({ theme }) => theme.spacing(1)};
    padding-left: ${({ theme }) => theme.spacing(2)};
`;

const UploadInfoText = styled(BodyMedium)`
    max-width: 400px;
    text-align: center;
    padding-left: ${({ theme }) => theme.spacing(1)};
    padding-right: ${({ theme }) => theme.spacing(1)};
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;
const UploadInfoTextSmall = styled(LabelSmall)`
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;

const UploadedErrorText = styled(UploadInfoText)`
    color: ${({ theme }) => theme.colors.error};
`;

const FileListItem = styled(Box, shouldForwardProps)<{
    hasDescriptionColumn?: boolean;
    isFile?: boolean;
}>`
    display: grid;
    width: 100%;
    grid-template-columns: ${({ hasDescriptionColumn, isFile }) =>
        `40px 1fr ${isFile ? '1fr' : ''} ${hasDescriptionColumn ? 'auto' : ''} 48px`};
    align-items: center;
`;

const FileName = styled(BodyLarge, shouldForwardProps)<{ hasError?: boolean }>`
    overflow: hidden;
    position: relative;
    display: inline-block;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ hasError, theme }) => (hasError ? theme.colors.error : theme.colors.text)};
`;

const FileSize = styled(LabelSmall)`
    margin-left: ${({ theme }) => theme.spacing(2)};
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
    overflow: hidden;
    position: relative;
    display: inline-block;
    text-overflow: ellipsis;
`;

const LinkName = styled(FileName)`
    text-decoration: underline;
    cursor: pointer;
`;

const CropModalContent = styled(Column)`
    gap: ${({ theme }) => theme.spacing(4)};
    margin-top: ${({ theme }) => theme.spacing(3)};
    margin-bottom: ${({ theme }) => theme.spacing(4)};
    overflow: auto;

    > .ReactCrop {
        position: relative;
        display: inline-block;
        cursor: crosshair;
        max-width: 100%;
        align-self: center;
        .ReactCrop__child-wrapper {
            // Sorry fot the ugly hack, but otherwise, we have an extra area below our image that causes a cropping bug
            margin-bottom: -6px;
        }

        svg {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            pointer-events: none;
        }

        .ReactCrop__crop-selection {
            position: absolute;
            top: 0;
            left: 0;
            transform: translateZ(0);
            cursor: move;

            .ReactCrop__drag-elements {
                .ReactCrop__drag-bar.ord-n {
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 12px;
                    transform: translateY(-50%);
                }

                // hide one sided resize handlers
                .ord-n,
                .ord-w,
                .ord-e,
                .ord-s {
                    display: none;
                }

                .ReactCrop__drag-handle {
                    position: absolute;
                    width: 12px;
                    height: 12px;
                    background-color: ${({ theme }) => theme.colors.disabled};
                    border: ${({ theme }) => `1px solid ${theme.colors.outlineVariant}`};

                    &.ord-nw {
                        top: 0;
                        left: 0;
                        transform: translate(-50%, -50%);
                        cursor: nw-resize;
                    }

                    &.ord-ne {
                        top: 0;
                        right: 0;
                        transform: translate(50%, -50%);
                        cursor: ne-resize;
                    }

                    &.ord-se {
                        bottom: 0;
                        right: 0;
                        transform: translate(50%, 50%);
                        cursor: se-resize;
                    }

                    &.ord-sw {
                        bottom: 0;
                        left: 0;
                        transform: translate(-50%, 50%);
                        cursor: sw-resize;
                    }
                }
            }
        }
        .ReactCrop__crop-selection:not(.ReactCrop--no-animate .ReactCrop__crop-selection) {
            animation: marching-ants 1s;
            animation-timing-function: ease;
            animation-iteration-count: 1;
            animation-play-state: running;
            background-image: ${({
                theme
            }) => `linear-gradient(to right, ${theme.colors.gradient1} 50%, ${theme.colors.gradient2} 50%),
                linear-gradient(to right, ${theme.colors.gradient1} 50%, ${theme.colors.gradient2} 50%),
                linear-gradient(to bottom, ${theme.colors.gradient1} 50%, ${theme.colors.gradient2} 50%),
                linear-gradient(to bottom, ${theme.colors.gradient1} 50%, ${theme.colors.gradient2} 50%)`};
            background-size:
                10px 1px,
                10px 1px,
                1px 10px,
                1px 10px;
            background-position:
                0 0,
                0 100%,
                0 0,
                100% 0;
            background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
            color: ${({ theme }) => theme.colors.gradient1};
            animation-play-state: running;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
        }
    }
`;
// max width/height calculation
// max-width = screenwidth substracted by 2x 24px padding and 2x 16px backdrop space
// max-height= crop modal has 85% height - substracted by 2x 24px padding, description text, title text and some backdrop space puffer
const CropImage = styled(Image, shouldForwardProps)<{ limiter?: 'width' | 'height' }>`
    width: ${({ limiter }) => (limiter === 'width' ? '100%' : 'auto')};
    height: ${({ limiter }) => (limiter === 'height' ? '100%' : 'auto')};
    max-width: calc(100vw - 80px);
    max-height: calc(85vh - 250px);
`;

const SourceInputs = styled(Box, shouldForwardProps)<{ breakpoint: Breakpoint }>`
    display: flex;
    align-items: center;
    flex-direction: ${({ breakpoint }) => (isCompact(breakpoint) ? 'column' : 'row')};
    margin-right: ${({ breakpoint, theme }) => theme.spacing(isCompact(breakpoint) ? 0 : 3)};
    width: ${({ breakpoint }) => `${getBreakpointValue(breakpoint, 200, 300, 425)}px`};
`;

const DataPrivacyText = styled(BodyMedium)`
    max-width: 533px;
    padding-top: ${({ theme }) => theme.spacing(3)};
    padding-bottom: ${({ theme }) => theme.spacing(8)};
    white-space: pre-line;
`;

export {
    CropImage,
    CropModalContent,
    DataPrivacyText,
    FileListItem,
    FileName,
    FileSize,
    LinkName,
    SourceInputs,
    UploadBox,
    UploadBoxSmall,
    UploadedErrorText,
    UploadInfoText,
    UploadInfoTextSmall
};
